import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ColumnFilter } from '@/components/ui/column-filter'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import Card from 'app/frontend/base/components/Card'
import ConfirmDialog from 'app/frontend/base/components/ConfirmDialog'
import Select from 'app/frontend/base/components/Select'
import type OccupancyStatus from 'app/frontend/types/custom/OccupancyStatus'
import {
  Check,
  CircleHelp,
  Heart,
  MessageCircleMore,
  Trash2,
  X,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import type { MapPOI } from '../utils'
import CharacterLabel from './CharacterLabel'
import {
  choiceTitles,
  houseIconUrl,
  rankedChoiceIconClass,
  statusClassName,
  statusVariant,
} from './housingUi'
import PlotOccupantLabel from './PlotOccupantLabel'
import type { CharacterRow, DisplayOccupant, RelevantCharacter } from './types'

type Props = {
  rows: CharacterRow[]
  plots: MapPOI[]
  occupantsByPlot: Record<string, DisplayOccupant[]>
  shardIndex: number
  isAdmin: boolean
  mutationPending: boolean
  onOpenPlot: (plot: MapPOI) => void
  onSetShardIndex: (shardIndex: number) => void
  onRequest: (
    rankedChoice: number,
    plotId: string | number | undefined,
    character?: RelevantCharacter,
  ) => void
  onWithdraw: (rankedChoice: number, character?: RelevantCharacter) => void
  onStatusChange: (occupantId: number, status: OccupancyStatus) => void
}

type SortKey = 'name' | 'claimed' | 1 | 2 | 3
type SortDirection = 'asc' | 'desc'
type ClaimedPlotFilter = 'settled' | 'homeless'
const plotActionButtonClassName =
  'inline-flex h-6 min-w-6 items-center justify-center px-1.5 text-sm font-medium transition-colors hover:bg-muted focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0'
const claimedPlotFilterOptions: Array<{
  label: string
  value: ClaimedPlotFilter
}> = [
  { label: 'Settled', value: 'settled' },
  { label: 'Homeless', value: 'homeless' },
]

const PlotCharacterTable = ({
  rows,
  plots,
  occupantsByPlot,
  shardIndex,
  isAdmin,
  mutationPending,
  onOpenPlot,
  onSetShardIndex,
  onRequest,
  onWithdraw,
  onStatusChange,
}: Props) => {
  const [sort, setSort] = useState<{
    key: SortKey
    direction: SortDirection
  }>({ key: 'name', direction: 'asc' })
  const [claimedPlotFilter, setClaimedPlotFilter] = useState<
    ClaimedPlotFilter[]
  >([])

  const filteredRows = useMemo(() => {
    if (claimedPlotFilter.length === 0) return rows

    return rows.filter((row) => {
      const hasClaimedPlot = Object.values(row.choices).some(
        (choice) => choice?.status === 'claimed',
      )

      return claimedPlotFilter.includes(hasClaimedPlot ? 'settled' : 'homeless')
    })
  }, [claimedPlotFilter, rows])

  const sortedRows = useMemo(() => {
    return [...filteredRows].sort((a, b) =>
      compareRows(a, b, sort.key, sort.direction, shardIndex),
    )
  }, [filteredRows, shardIndex, sort])

  const updateSort = (key: SortKey) => {
    setSort((current) => ({
      key,
      direction:
        current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }))
  }
  return (
    <Card className='overflow-x-auto p-0'>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              <span
                className='cursor-pointer'
                onClick={() => updateSort('name')}
              >
                Character
              </span>
            </TableHead>
            <TableHead className='w-20'>
              <span className='flex items-center gap-2 whitespace-nowrap'>
                <button
                  type='button'
                  className='flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center'
                  onClick={() => updateSort('claimed')}
                >
                  <img
                    src={houseIconUrl}
                    alt='house'
                    className='h-5 w-5 max-w-none shrink-0 object-contain'
                  />
                </button>
                <ColumnFilter
                  value={claimedPlotFilter}
                  onChange={(value) =>
                    setClaimedPlotFilter(value as ClaimedPlotFilter[])
                  }
                  options={claimedPlotFilterOptions}
                  idPrefix='claimed-plot-filter'
                />
              </span>
            </TableHead>
            {[1, 2, 3].map((rankedChoice) => (
              <TableHead key={rankedChoice} className='min-w-64'>
                <button
                  type='button'
                  className='flex items-center gap-2'
                  onClick={() => updateSort(rankedChoice as 1 | 2 | 3)}
                >
                  <span className='flex items-center gap-2'>
                    <Heart
                      className={`h-4 w-4 ${rankedChoiceIconClass(
                        rankedChoice,
                      )}`}
                    />
                    {choiceTitles[rankedChoice]} choice
                  </span>
                </button>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedRows.map((row) => {
            const claimedPlot = Object.values(row.choices).find(
              (choice) => choice?.status === 'claimed',
            )

            return (
              <TableRow key={row.character.rowKey}>
                <TableCell>
                  <div className='flex items-center gap-2'>
                    <CharacterLabel character={row.character} />
                    {row.choices[4] &&
                    Object.keys(row.choices).length === 1 &&
                    !row.character.inTeam ? (
                      <Button
                        size='xs'
                        variant='ghost'
                        className='p-2 h-7'
                        disabled={mutationPending}
                        onClick={() => onWithdraw(4, row.character)}
                      >
                        <Trash2 className='h-4 w-4 text-destructive-foreground' />
                      </Button>
                    ) : null}
                  </div>
                </TableCell>
                <TableCell className='text-left'>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className='inline-flex items-center gap-1'>
                        <img
                          src={houseIconUrl}
                          alt='house'
                          className={`h-7 w-7 shrink-0 object-contain ${
                            claimedPlot ? '' : 'grayscale opacity-50'
                          }`}
                        />
                        <span>{claimedPlot?.plot_id}</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      {claimedPlot
                        ? `Settled on plot ${claimedPlot.plot_id}${
                            claimedPlot.shard_index !== shardIndex
                              ? ` (in subdivision ${
                                  claimedPlot.shard_index + 1
                                })`
                              : ''
                          }`
                        : 'Homeless'}
                    </TooltipContent>
                  </Tooltip>
                </TableCell>
                {[1, 2, 3].map((rankedChoice) => (
                  <TableCell key={rankedChoice} className='group'>
                    <ChoiceCell
                      rankedChoice={rankedChoice}
                      choice={row.choices[rankedChoice]}
                      character={row.character}
                      plots={plots}
                      occupantsByPlot={occupantsByPlot}
                      shardIndex={shardIndex}
                      isAdmin={isAdmin}
                      mutationPending={mutationPending}
                      onOpenPlot={onOpenPlot}
                      onSetShardIndex={onSetShardIndex}
                      onRequest={onRequest}
                      onWithdraw={onWithdraw}
                      onStatusChange={onStatusChange}
                    />
                  </TableCell>
                ))}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </Card>
  )
}

const compareRows = (
  a: CharacterRow,
  b: CharacterRow,
  sortKey: SortKey,
  direction: SortDirection,
  shardIndex: number,
) => {
  const multiplier = direction === 'asc' ? 1 : -1
  if (sortKey === 'name') {
    return multiplier * a.character.name.localeCompare(b.character.name)
  }

  if (typeof sortKey === 'number') {
    const aChoice = a.choices[sortKey]
    const bChoice = b.choices[sortKey]

    if (!aChoice && !bChoice) return 0
    if (!aChoice) return 1
    if (!bChoice) return -1

    const aMatchesShard = aChoice.shard_index === shardIndex
    const bMatchesShard = bChoice.shard_index === shardIndex

    if (aMatchesShard !== bMatchesShard) {
      return aMatchesShard ? -1 : 1
    }
  }

  const comparison = comparePlotValues(
    plotIdForSort(a, sortKey),
    plotIdForSort(b, sortKey),
    direction,
  )
  return comparison === 0
    ? a.character.name.localeCompare(b.character.name)
    : comparison
}

const plotIdForSort = (row: CharacterRow, sortKey: SortKey) => {
  if (sortKey === 'name') return undefined
  if (sortKey === 'claimed') {
    return Object.values(row.choices).find(
      (choice) => choice?.status === 'claimed',
    )?.plot_id
  }

  return row.choices[sortKey]?.plot_id
}

const comparePlotValues = (
  aPlotId: number | string | undefined,
  bPlotId: number | string | undefined,
  direction: SortDirection,
) => {
  if (aPlotId === undefined && bPlotId === undefined) return 0
  if (aPlotId === undefined) return 1
  if (bPlotId === undefined) return -1

  const multiplier = direction === 'asc' ? 1 : -1
  return multiplier * (Number(aPlotId) - Number(bPlotId))
}

type ChoiceCellProps = {
  rankedChoice: number
  choice?: DisplayOccupant
  character: RelevantCharacter
  plots: MapPOI[]
  occupantsByPlot: Record<string, DisplayOccupant[]>
  shardIndex: number
  isAdmin: boolean
  mutationPending: boolean
  onOpenPlot: (plot: MapPOI) => void
  onSetShardIndex: (shardIndex: number) => void
  onRequest: (
    rankedChoice: number,
    plotId: string | number | undefined,
    character?: RelevantCharacter,
  ) => void
  onWithdraw: (rankedChoice: number, character?: RelevantCharacter) => void
  onStatusChange: (occupantId: number, status: OccupancyStatus) => void
}

const ChoiceCell = ({
  rankedChoice,
  choice,
  character,
  plots,
  occupantsByPlot,
  shardIndex,
  isAdmin,
  mutationPending,
  onOpenPlot,
  onSetShardIndex,
  onRequest,
  onWithdraw,
  onStatusChange,
}: ChoiceCellProps) => {
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false)

  if (!choice) {
    if (!isAdmin || character.userHasApprovedPlot) return null

    return (
      <div className='opacity-0 transition-opacity duration-200 delay-50 group-hover:opacity-100 group-focus-within:opacity-100'>
        <Select
          value={undefined}
          setValue={(plotId) => onRequest(rankedChoice, plotId, character)}
          disabled={mutationPending}
          className='h-8'
          options={plots.map((plot) => ({
            value: plot.Name,
            label: `Plot ${plot.Name}`,
            customOptionFn: () => (
              <div className='flex w-full items-center justify-between gap-3'>
                <span className='grow'>Plot {plot.Name}</span>
                <span className='shrink-0'>
                  <PlotOccupantLabel
                    occupants={(occupantsByPlot[plot.Name] ?? []).filter(
                      (occupant) => occupant.shard_index === shardIndex,
                    )}
                  />
                </span>
              </div>
            ),
            sortLabel: plot.Name.padStart(3, '0'),
          }))}
          placeholder='Choose plot'
        />
      </div>
    )
  }

  const plot = plots.find((poi) => poi.Name === choice.plot_id.toString())
  const occupantsForPlot = (
    occupantsByPlot[choice.plot_id.toString()] ?? []
  ).filter((occupant) => occupant.shard_index === shardIndex)
  const obsolete =
    choice.character.userHasApprovedPlot && choice.status !== 'approved'
  const plotLabelClassName =
    choice.status === 'rejected' ? 'opacity-50' : undefined

  if (choice.shard_index !== shardIndex) {
    return (
      <button
        type='button'
        className={`text-sm ${statusClassName[choice.status]} ${
          obsolete ? 'opacity-40' : ''
        }`}
        onClick={() => onSetShardIndex(choice.shard_index)}
      >
        In subdivision {choice.shard_index + 1}
      </button>
    )
  }

  return (
    <Card
      className={`flex items-center justify-between gap-4 rounded-md border px-2 py-0.5 ${
        obsolete ? 'opacity-40' : ''
      }`}
    >
      <button
        type='button'
        className='flex min-w-0 flex-1 items-center justify-between gap-3 text-left'
        onClick={() => plot && onOpenPlot(plot)}
      >
        <span className='flex items-center gap-2 truncate'>
          <span className={plotLabelClassName}>
            Plot {plot?.Name ?? choice.plot_id}
          </span>
          {choice.note ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <MessageCircleMore className='h-4 w-4 text-muted-foreground' />
              </TooltipTrigger>
              <TooltipContent className='max-w-80 whitespace-pre-wrap'>
                {choice.note}
              </TooltipContent>
            </Tooltip>
          ) : null}
        </span>
        <PlotOccupantLabel
          occupants={occupantsForPlot}
          forceSummary='partial'
        />
      </button>

      {isAdmin ? (
        <div className='flex items-center gap-1'>
          <Card className='overflow-hidden p-0'>
            <div className='flex items-center justify-center gap-0'>
              <button
                type='button'
                aria-label='Approve choice'
                aria-pressed={choice.status === 'approved'}
                disabled={mutationPending}
                className={`${plotActionButtonClassName} rounded-l-md hover:[&_svg]:text-success-foreground ${
                  choice.status === 'approved'
                    ? 'bg-success [&_svg]:text-success-foreground'
                    : ''
                }`}
                onClick={() => {
                  if (choice.status !== 'approved') {
                    onStatusChange(choice.id, 'approved')
                  }
                }}
              >
                <Check className='h-4 w-4 text-muted-foreground' />
              </button>
              <button
                type='button'
                aria-label='Mark choice requested'
                aria-pressed={choice.status === 'requested'}
                disabled={mutationPending}
                className={`${plotActionButtonClassName} hover:[&_svg]:text-warning-foreground ${
                  choice.status === 'requested'
                    ? 'bg-warning [&_svg]:text-warning-foreground'
                    : ''
                }`}
                onClick={() => {
                  if (choice.status !== 'requested') {
                    onStatusChange(choice.id, 'requested')
                  }
                }}
              >
                <CircleHelp className='h-4 w-4 text-muted-foreground' />
              </button>
              <button
                type='button'
                aria-label='Reject choice'
                aria-pressed={choice.status === 'rejected'}
                disabled={mutationPending}
                className={`${plotActionButtonClassName} hover:[&_svg]:text-destructive-foreground ${
                  choice.status === 'rejected'
                    ? 'bg-destructive [&_svg]:text-destructive-foreground'
                    : ''
                }`}
                onClick={() => {
                  if (choice.status !== 'rejected') {
                    onStatusChange(choice.id, 'rejected')
                  }
                }}
              >
                <X className='h-4 w-4 text-muted-foreground' />
              </button>
              <button
                type='button'
                aria-label='Delete choice'
                disabled={mutationPending}
                className={`${plotActionButtonClassName} rounded-r-md hover:[&_svg]:text-destructive-foreground`}
                onClick={() => setDeleteConfirmationOpen(true)}
              >
                <Trash2 className='h-4 w-4 text-muted-foreground' />
              </button>
            </div>
          </Card>
          <ConfirmDialog
            open={deleteConfirmationOpen}
            description='Are you sure you want to delete this choice entirely, without feedback to the user?'
            continueText='Delete'
            variant='destructive'
            onCancel={() => setDeleteConfirmationOpen(false)}
            onContinue={() => {
              setDeleteConfirmationOpen(false)
              onWithdraw(rankedChoice, character)
            }}
          />
        </div>
      ) : (
        <Badge variant={statusVariant[choice.status]}>{choice.status}</Badge>
      )}
    </Card>
  )
}

export default PlotCharacterTable
